/**
 * Premium calculation utilities for Change Plan amendment
 *
 * Member Categories:
 * 1. Nuclear members: Principal, Spouse, Child
 *    - Use nuclear plan
 *    - Principal pays family premium, Spouse/Child get premium = 0 for main benefits
 *    - Each member pays their own additional benefits
 *
 * 2. Parent members: Parent, Parent_In_Law
 *    - Use parent plan with age-based pricing
 *    - Each parent pays their own premium based on age
 *
 * 3. Extended members: Nephew, Brother, etc.
 *    - Independent of plan changes
 *    - Benefits and premiums remain unchanged
 */
import { v4 as uuidv4 } from 'uuid';
import { getNuclearPremium, getNuclearBenefit, getAdditionalBenefits } from '../../../../PolicyAdmin/helpers/index';
import { getExtendedMemberConfig, isExtended2Member } from './planHelpers';
import { addAmendmentTracking } from '../../shared/utils/amendmentTrackingHelper';

// ============================================================================
// CONSTANTS
// ============================================================================

const BENEFIT_TYPES = {
  FUNERAL: 'FUNERAL BENEFIT',
  ACCIDENTAL: 'ACCIDENTAL BENEFIT',
  GROUP_LIFE: 'Group Life Assurance'
};

const MEMBER_RELATIONSHIPS = {
  NUCLEAR: ['Principal', 'Spouse', 'Child'],
  PARENT: ['Parent', 'Parent_In_Law']
};

// Benefits that are hardcoded (not plan-specific) and should always be preserved
const HARDCODED_BENEFITS = [BENEFIT_TYPES.GROUP_LIFE];

const ACCIDENTAL_BENEFIT_CAP = 20000 * 100; // BWP 20,000 cap in cents

const DEBUG = false; // Set to true to enable verbose logging

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract premium from benefit object
 * Premium can be stored in either premium_amount or additional_details.premium_amount
 */
const getBenefitPremium = (benefit) => {
  return benefit.premium_amount || (benefit.additional_details && benefit.additional_details.premium_amount) || 0;
};

/**
 * Calculate total premium for a member from their benefits
 */
const calculateMemberTotalPremium = (benefits) => {
  return benefits.reduce((sum, b) => sum + getBenefitPremium(b), 0);
};

/**
 * Check if a benefit is hardcoded (independent of plan)
 */
const isHardcodedBenefit = (benefitType) => {
  return HARDCODED_BENEFITS.includes(benefitType);
};

/**
 * Log debug information (only if DEBUG is enabled)
 */
const debugLog = (message, data) => {
  if (DEBUG) {
    if (data) {
      console.log(message, data);
    } else {
      console.log(message);
    }
  }
};

// ============================================================================
// BENEFIT PROCESSING
// ============================================================================

/**
 * Process additional benefits (waivers, add-ons, etc.)
 * Matches existing benefits with new plan benefits and reprices or drops them
 *
 * Special case: Hardcoded benefits (e.g., Group Life Assurance) are always preserved
 */
const processAdditionalBenefits = (member, existingBenefits, newPlan, lookupKey, droppedBenefits) => {
  if (!existingBenefits || existingBenefits.length === 0) {
    return [];
  }

  const newBenefits = [];
  const availableBenefits = getAdditionalBenefits(lookupKey, newPlan);

  debugLog('  Existing Additional Benefits:', existingBenefits.map(b => ({
    type: b.type,
    premium: getBenefitPremium(b)
  })));
  debugLog('  Available in New Plan (lookup key:', lookupKey, '):', availableBenefits.map(b => ({
    type: b.type,
    premium: b.premium_amount
  })));

  existingBenefits.forEach(oldBenefit => {
    const oldPremium = getBenefitPremium(oldBenefit);

    // Special case: Hardcoded benefits are independent of plan
    if (isHardcodedBenefit(oldBenefit.type)) {
      debugLog(`  ✓ Preserving "${oldBenefit.type}": ${oldPremium} (hardcoded benefit)`);
      newBenefits.push(oldBenefit);
      return;
    }

    const matchingBenefit = availableBenefits.find(b => b.type === oldBenefit.type);

    if (matchingBenefit) {
      // Benefit exists in new plan - reprice it
      debugLog(`  ✓ Repricing "${oldBenefit.type}": ${oldPremium} → ${matchingBenefit.premium_amount}`);
      newBenefits.push({
        ...oldBenefit,
        premium_amount: matchingBenefit.premium_amount,
        cover_amount_in_cents: matchingBenefit.cover_amount_in_cents || oldBenefit.cover_amount_in_cents,
        additional_details: {
          ...oldBenefit.additional_details,
          premium_amount: matchingBenefit.premium_amount
        }
      });
    } else {
      // Benefit not available in new plan - drop it
      console.log(`  ✗ Dropping "${oldBenefit.type}" (not available in new plan)`);
      droppedBenefits.push({
        memberName: `${member.first_name} ${member.last_name}`,
        relationship: member.relationship,
        benefitType: oldBenefit.type,
        coverAmount: oldBenefit.cover_amount_in_cents,
        premium: oldPremium
      });
    }
  });

  return newBenefits;
};

/**
 * Create a standard benefit object with amendment tracking
 * @param {String} type - Benefit type
 * @param {Number} coverAmount - Cover amount in cents
 * @param {Number} premium - Premium amount
 * @param {Object} existingBenefit - Existing benefit (for uuid preservation and tracking)
 * @param {Object} oldQuotation - Old quotation (for tracking previous plan)
 * @param {String} newPlan - New plan code (e.g., "Plan_7")
 */
const createBenefit = (type, coverAmount, premium, existingBenefit, oldQuotation, newPlan) => {
  const benefitUuid = existingBenefit ? existingBenefit.uuid : uuidv4();

  // Base benefit object
  let newBenefit = {
    type: type,
    cover_amount_in_cents: coverAmount,
    waiting_period_in_days: 30,
    cover_usage_count: 0,
    benefit_period_cardinality: 1,
    benefit_period_granuality: "year",
    premium_amount: premium,
    is_initialized: true,
    uuid: benefitUuid,
    additional_details: {
      premium_amount: premium,
      uuid: benefitUuid,
    }
  };

  // Add amendment tracking if we have existing benefit and quotation info
  if (existingBenefit && oldQuotation && newPlan) {
    newBenefit = addAmendmentTracking(newBenefit, existingBenefit, oldQuotation, newPlan, 'change_plan');
  }

  return newBenefit;
};

// ============================================================================
// MEMBER BENEFIT RECALCULATION
// ============================================================================

/**
 * Recalculate benefits for nuclear members (Principal, Spouse, Child)
 *
 * Premium Logic:
 * - Main benefits (FUNERAL, ACCIDENTAL): Only Principal pays, Spouse/Child = 0
 * - Additional benefits (Waivers, etc.): Each member pays their own
 */
const recalculateNuclearMemberBenefits = (member, nuclearPlan, coverType, droppedBenefits, oldQuotation) => {
  const planPremium = getNuclearPremium(nuclearPlan, coverType);
  const planBenefit = getNuclearBenefit(nuclearPlan);
  const isPrincipal = member.relationship === 'Principal';
  const newPlanCode = nuclearPlan.code || nuclearPlan.name.replace(/ /g, '_');

  console.log(`Processing: ${member.first_name} (${member.relationship}) - Plan: ${nuclearPlan.name}`);

  // Main benefit premium: Only Principal pays
  const funeralPremium = isPrincipal ? planPremium : 0;

  const newBenefits = [];
  const existingFuneral = member.benefits.find(b => b.type === BENEFIT_TYPES.FUNERAL);
  const existingAccidental = member.benefits.find(b => b.type === BENEFIT_TYPES.ACCIDENTAL);

  // 1. FUNERAL BENEFIT - with new plan values and amendment tracking
  newBenefits.push(createBenefit(
    BENEFIT_TYPES.FUNERAL,
    planBenefit,
    funeralPremium,
    existingFuneral,
    oldQuotation,
    newPlanCode
  ));

  // 2. ACCIDENTAL BENEFIT - always premium = 0, capped at BWP 20,000
  newBenefits.push(createBenefit(
    BENEFIT_TYPES.ACCIDENTAL,
    planBenefit > ACCIDENTAL_BENEFIT_CAP ? ACCIDENTAL_BENEFIT_CAP : planBenefit,
    0,
    existingAccidental,
    oldQuotation,
    newPlanCode
  ));

  // 3. Additional benefits - each member pays their own
  const existingAdditionalBenefits = member.benefits.filter(b =>
    b.type !== BENEFIT_TYPES.FUNERAL && b.type !== BENEFIT_TYPES.ACCIDENTAL
  );

  const additionalBenefits = processAdditionalBenefits(
    member,
    existingAdditionalBenefits,
    nuclearPlan,
    coverType, // Nuclear members lookup by cover type
    droppedBenefits
  );

  newBenefits.push(...additionalBenefits);

  const totalPremium = calculateMemberTotalPremium(newBenefits);
  debugLog(`  Total Premium: ${totalPremium}`);

  return newBenefits;
};

/**
 * Recalculate benefits for parent members (Parent, Parent_In_Law)
 *
 * Premium Logic:
 * - All parent members use age-based pricing from parent plan
 * - Each parent pays their own premium based on their age
 */
const recalculateParentMemberBenefits = (member, parentPlan, droppedBenefits, oldQuotation) => {
  console.log(`Processing: ${member.first_name} (${member.relationship}) - Plan: ${parentPlan.name}`);
  const newPlanCode = parentPlan.code || parentPlan.name.replace(/ /g, '_');

  // Get age-based premium and benefit from parent plan
  const ageConfig = getExtendedMemberConfig(parentPlan, member.date_of_birth);

  if (!ageConfig) {
    console.error(`  ERROR: No age-based config found for DOB: ${member.date_of_birth}`);
    return member.benefits; // Return original if config not found
  }

  debugLog(`  Age-based Premium: ${ageConfig.premium_rate}, Benefit: ${ageConfig.benefit_amount}`);

  const newBenefits = [];
  const existingFuneral = member.benefits.find(b => b.type === BENEFIT_TYPES.FUNERAL);

  // 1. FUNERAL BENEFIT - with age-based values from parent plan and tracking
  newBenefits.push(createBenefit(
    BENEFIT_TYPES.FUNERAL,
    ageConfig.benefit_amount,
    ageConfig.premium_rate,
    existingFuneral,
    oldQuotation,
    newPlanCode
  ));

  // 2. Additional benefits - repriced from parent plan
  const existingAdditionalBenefits = member.benefits.filter(b => b.type !== BENEFIT_TYPES.FUNERAL);

  const additionalBenefits = processAdditionalBenefits(
    member,
    existingAdditionalBenefits,
    parentPlan,
    member.relationship, // Parent members lookup by relationship
    droppedBenefits
  );

  newBenefits.push(...additionalBenefits);

  const totalPremium = calculateMemberTotalPremium(newBenefits);
  debugLog(`  Total Premium: ${totalPremium}`);

  return newBenefits;
};

// ============================================================================
// MAIN CALCULATION FUNCTION
// ============================================================================

/**
 * Calculate new premiums for all members based on plan change
 *
 * @param {Array} members - All policy members
 * @param {Object} newNuclearPlan - New nuclear plan (for Principal, Spouse, Child)
 * @param {Object} newParentPlan - New parent plan (for Parent, Parent_In_Law)
 * @param {Object} policy - Policy object
 * @returns {Object} { updatedMembers: Array, droppedBenefits: Array }
 */
export const calculateNewPremiums = (members, newNuclearPlan, newParentPlan, policy) => {
  const coverType = (policy.quotation && policy.quotation.additional_details && policy.quotation.additional_details.cover_type) || 'Member & Family';
  const oldQuotation = policy.quotation; // For amendment tracking

  console.log('\n=== CALCULATE NEW PREMIUMS ===');
  console.log(`Cover Type: ${coverType}`);
  console.log(`Nuclear Plan: ${newNuclearPlan.name} (${newNuclearPlan.code})`);
  console.log(`Parent Plan: ${newParentPlan.name} (${newParentPlan.code})\n`);

  const droppedBenefits = [];

  const updatedMembers = members.map(member => {
    const { relationship } = member;

    // 1. Nuclear members: Principal, Spouse, Child
    if (MEMBER_RELATIONSHIPS.NUCLEAR.includes(relationship)) {
      const newBenefits = recalculateNuclearMemberBenefits(member, newNuclearPlan, coverType, droppedBenefits, oldQuotation);
      const newPremium = calculateMemberTotalPremium(newBenefits);

      return {
        ...member,
        benefits: newBenefits,
        premium_amount_in_cents: newPremium
      };
    }

    // 2. Parent members: Parent, Parent_In_Law
    if (MEMBER_RELATIONSHIPS.PARENT.includes(relationship)) {
      const newBenefits = recalculateParentMemberBenefits(member, newParentPlan, droppedBenefits, oldQuotation);
      const newPremium = calculateMemberTotalPremium(newBenefits);

      return {
        ...member,
        benefits: newBenefits,
        premium_amount_in_cents: newPremium
      };
    }

    // 3. Extended members: Nephew, Brother, etc. - unchanged
    if (isExtended2Member(relationship)) {
      debugLog(`  Extended Member (unchanged): ${member.first_name} (${relationship})`);
      return member;
    }

    // Default: unchanged
    return member;
  });

  console.log('=== CALCULATION COMPLETE ===\n');

  return {
    updatedMembers,
    droppedBenefits
  };
};

// ============================================================================
// QUOTATION BUILDER
// ============================================================================

/**
 * Build quotation payload for plan change API
 *
 * @param {Object} policy - Original policy object
 * @param {Array} updatedMembers - Members with recalculated premiums
 * @param {String} newNuclearPlanCode - New nuclear plan code (e.g., "Plan_7")
 * @param {String} newParentPlanCode - New parent plan code (e.g., "Plan_7")
 * @returns {Object} Quotation payload for API
 */
export const buildChangePlanQuotation = (policy, updatedMembers, newNuclearPlanCode, newParentPlanCode) => {
  // Build policy_schedule_entries from updated members
  // Set waiting_period_in_days to 150 for all members' benefits (plan change affects everyone)
  const policy_schedule_entries = updatedMembers.map(member => {
    const benefits = (member.benefits || []).map(benefit => ({
      ...benefit,
      waiting_period_in_days: 150
    }));

    return {
      guid: member.guid,
      first_name: member.first_name,
      middle_name: member.middle_name || '',
      last_name: member.last_name,
      msisdn: member.msisdn || member.mobile_number,
      date_of_birth: member.date_of_birth,
      gender: member.gender,
      national_id: member.national_id || member.id_number || 'N/A',
      passport: member.passport || 'N/A',
      postal_address: member.postal_address || '',
      relationship: member.relationship,
      residential_address: member.residential_address || '',
      type: member.type || 'Nuclear',
      premium_amount_in_cents: member.premium_amount_in_cents || 0,
      extended_premium: member.extended_premium || 0,
      add_on_premium: member.add_on_premium || 0,
      benefits: benefits,
      uuid: member.uuid,
      additional_details: member.additional_details || {}
    };
  });

  // Preserve extended_members_benefit for Extended_2 members
  const existingExtendedBenefit = policy.quotation && policy.quotation.additional_details
    ? policy.quotation.additional_details.extended_members_benefit
    : null;

  return {
    product_guid: policy.product_guid,
    product_name: policy.product_name,
    product_code: policy.product_code,
    branch: policy.branch || 'string',
    customer_guid: policy.customer_guid,
    partner: policy.partner,
    lapse_period: policy.lapse_period || 12,
    lapse_period_granularity: policy.lapse_period_granularity || 'month',
    lapse_period_cardinality: policy.lapse_period_cardinality || 1,
    premium_granularity: policy.premium_granularity || 'month',
    premium_cardinality: policy.premium_cardinality || 1,
    policy_schedule_entries: policy_schedule_entries,
    additional_details: {
      ...policy.quotation.additional_details,
      primary_plan: newNuclearPlanCode,
      secondary_plan: newParentPlanCode,
      cover_type: (policy.quotation && policy.quotation.additional_details && policy.quotation.additional_details.cover_type) || 'Member & Family',
      extended_members_benefit: existingExtendedBenefit
    }
  };
};
